import * as React from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type SortingState,
  type VisibilityState,
  type PaginationState,
} from "@tanstack/react-table";
import {
  DataTablePagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  useToast,
} from "@repo/ui";
import { cn } from "@repo/utils";
import type {
  Product,
  DispenseFormData,
  ReceiptFormData,
} from "@/lib/types/inventory";
import { showSuccessToast } from "@/lib/utils/error-display";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { useApproval } from "@/context/approval-context";
import { trackAction, logger } from "@/lib/utils/logger";
import { createDispense } from "@/lib/api/dispenses";
import { createNote } from "@/lib/api/notes";
import { createReceipt } from "@/lib/api/receipts";
import { api, ApiClientError } from "@/lib/api/client";
import { createColumns } from "./columns";
import { InventoryToolbar } from "./inventory-toolbar";
import { DrugDetailsDrawer } from "./drug-details-drawer";
import { InventoryAuditModal } from "./inventory-audit-modal";
// import { ActivityDrawer } from "./activity-drawer"; // REMOVED: Now redirects to dedicated activity tab
import { NotesThread } from "@/components/notes-thread";
import { DeleteInventoryModal } from "./delete-inventory-modal";
import { DispenseFormPanel } from "@/components/dispenses/dispense-form-panel";
import type { DispenseCreateContext } from "@/components/dispenses/dispense-form-panel";
import { getPharmacyOptions } from "@/lib/api/pharmacy";
import { ReceiptFormPanel } from "@/components/receipts/receipt-form-panel";
import { InventoryRowExpandedPanel } from "./inventory-row-expanded-panel";
import { Package } from "lucide-react";
import { usePaginatedData } from "@/hooks";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3005";

interface InventoryTableProps {
  onRefresh?: () => void;
  onStartBulkAdd?: () => void;
  userRole?: string;
  initialSearch?: string;
  initialDispense?: { ndc: string; itemName: string };
}

export function InventoryTable({
  onRefresh,
  onStartBulkAdd,
  userRole,
  initialSearch,
  initialDispense,
}: InventoryTableProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();

  // Server-side pagination hook
  const {
    data,
    pagination: paginationInfo,
    isLoading,
    setPage,
    setPageSize,
    setSorting: setServerSorting,
    setSearch,
    setFilters,
    refetch,
  } = usePaginatedData<Product>({
    endpoint: "/inventory",
    defaultPageSize: 50,
    defaultSortBy: "item_name",
    defaultSortOrder: "asc",
  });

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [selectedNDC, setSelectedNDC] = React.useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = React.useState<Product | null>(
    null
  );
  const [drugDetailsOpen, setDrugDetailsOpen] = React.useState(false);
  const [drawerMode, setDrawerMode] = React.useState<"view" | "edit">("view");
  const [editingItem, setEditingItem] = React.useState<Product | null>(null);

  // Pagination state for TanStack Table
  const [{ pageIndex, pageSize }, setPagination] =
    React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 50,
    });

  // Expanded row state
  const [expandedRowId, setExpandedRowId] = React.useState<string | null>(null);

  // Inline editing state
  const [editingRowId, setEditingRowId] = React.useState<string | null>(null);
  const [editingData, setEditingData] = React.useState<{
    drug_name: string;
    drug_type: "schedule_ii" | "schedule_iii" | "schedule_iv" | "schedule_v";
    reorder_point: number;
  } | null>(null);
  const editingDataRef = React.useRef(editingData);
  const [isSaving, setIsSaving] = React.useState(false);

  // Keep ref in sync with state
  React.useEffect(() => {
    editingDataRef.current = editingData;
  }, [editingData]);

  // Apply initial search from URL param (e.g. navigating from activity by NDC)
  const initialSearchApplied = React.useRef(false);
  React.useEffect(() => {
    if (initialSearch && !initialSearchApplied.current) {
      initialSearchApplied.current = true;
      setSearch(initialSearch);
    }
  }, [initialSearch, setSearch]);

  // Inventory audit modal state
  const [auditModalOpen, setAuditModalOpen] = React.useState(false);
  const [preselectedItemId, setPreselectedItemId] = React.useState<
    string | null
  >(null);

  // Activity drawer state - REMOVED: Now redirects to activity tab instead
  // const [activityDrawerOpen, setActivityDrawerOpen] = React.useState(false);
  // const [activityItem, setActivityItem] = React.useState<{
  //   id: string;
  //   name: string;
  //   ndc: string;
  // } | null>(null);

  // Notes modal state
  const [notesModalOpen, setNotesModalOpen] = React.useState(false);
  const [notesItem, setNotesItem] = React.useState<Product | null>(null);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [deleteItem, setDeleteItem] = React.useState<{
    id: string;
    ndc: string;
    item_name: string;
  } | null>(null);

  // Dispense form panel state
  const [dispenseFormOpen, setDispenseFormOpen] = React.useState(false);
  const [dispensePrefill, setDispensePrefill] = React.useState<
    { ndc: string; itemName: string; date: Date } | undefined
  >(undefined);

  // Auto-open dispense form when navigated with initialDispense params
  const initialDispenseHandled = React.useRef(false);
  React.useEffect(() => {
    if (initialDispense && !initialDispenseHandled.current) {
      initialDispenseHandled.current = true;
      setDispensePrefill({
        ndc: initialDispense.ndc,
        itemName: initialDispense.itemName,
        date: new Date(),
      });
      setDispenseFormOpen(true);
    }
  }, [initialDispense]);

  // Confirm stock on dispense
  const [confirmStockEnabled, setConfirmStockEnabled] = React.useState(false);

  React.useEffect(() => {
    getPharmacyOptions()
      .then((res) => {
        setConfirmStockEnabled(res.data.confirm_stock_on_dispense);
      })
      .catch(() => {
        setConfirmStockEnabled(false);
      });
  }, []);

  // Receipt form panel state
  const [receiptFormOpen, setReceiptFormOpen] = React.useState(false);
  const [receiptPrefill, setReceiptPrefill] = React.useState<
    { ndc: string; itemName: string; dateReceived: Date } | undefined
  >(undefined);

  const handleNDCClick = (ndc: string) => {
    const product = data.find((item) => item.ndc === ndc) ?? null;
    setSelectedNDC(ndc);
    setSelectedProduct(product);
    setDrawerMode("view");
    setEditingItem(null);
    setDrugDetailsOpen(true);
  };

  const handleViewDetails = (product: Product) => {
    setSelectedNDC(product.ndc);
    setSelectedProduct(product);
    setDrawerMode("view");
    setEditingItem(null);
    setDrugDetailsOpen(true);
  };

  const handleRowClick = React.useCallback(
    (rowId: string) => {
      if (editingRowId) return;
      setExpandedRowId((prev) => (prev === rowId ? null : rowId));
    },
    [editingRowId]
  );

  const handleEdit = React.useCallback((item: Product) => {
    setExpandedRowId(null);
    // Start inline editing
    setEditingRowId(item.id || "");
    setEditingData({
      drug_name: item.item_name,
      drug_type: item.drug_type,
      reorder_point: item.reorder_threshold,
    });
  }, []);

  const handleCancelEdit = React.useCallback(() => {
    setEditingRowId(null);
    setEditingData(null);
  }, []);

  const handleSaveEdit = React.useCallback(
    async (item: Product, approvalToken?: string) => {
      const currentEditingData = editingDataRef.current;
      if (!currentEditingData || !item.id) {
        toast({
          title: "Error",
          description: "No data to save",
          variant: "destructive",
        });
        return;
      }

      setIsSaving(true);
      try {
        await api.patch(`/inventory/${item.id}`, {
          ...currentEditingData,
          ...(approvalToken ? { approvalToken } : {}),
        });

        toast({
          title: "Success",
          description: "Inventory item updated successfully",
          variant: "success",
        });

        setEditingRowId(null);
        setEditingData(null);
        refetch();
        onRefresh?.();
      } catch (error) {
        if (error instanceof ApiClientError) {
          if (
            handlePermissionError(error, {
              requestApproval,
              toast,
              retryFn: async (token) => handleSaveEdit(item, token),
              actionDescription: `Edit inventory item ${item.item_name}`,
            })
          )
            return;
        }
        toast({
          title: "Error",
          description:
            error instanceof Error ? error.message : "Failed to update item",
          variant: "destructive",
        });
      } finally {
        setIsSaving(false);
      }
    },
    [onRefresh, toast, refetch, requestApproval]
  );

  const handleFieldChange = React.useCallback(
    (field: "drug_name" | "drug_type" | "reorder_point", value: string) => {
      setEditingData((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          [field]: field === "reorder_point" ? parseInt(value) || 0 : value,
        };
      });
    },
    []
  );

  const handleAuditStock = React.useCallback((itemId: string) => {
    setPreselectedItemId(itemId);
    setAuditModalOpen(true);
  }, []);

  const handleAuditSuccess = () => {
    setPreselectedItemId(null);
    // Exit edit mode after audit
    setEditingRowId(null);
    setEditingData(null);
    refetch();
    onRefresh?.();
  };

  const handleEditSuccess = () => {
    refetch();
    onRefresh?.();
  };

  // REMOVED: Activity now redirects to dedicated activity tab
  // const handleViewActivity = React.useCallback((item: Product) => {
  //   setActivityItem({
  //     id: item.id || "",
  //     name: item.item_name,
  //     ndc: item.ndc,
  //   });
  //   setActivityDrawerOpen(true);
  // }, []);

  const handleEditNotes = React.useCallback((item: Product) => {
    setNotesItem(item);
    setNotesModalOpen(true);
  }, []);

  const handleToggleHidden = React.useCallback(
    async (item: Product, approvalToken?: string) => {
      try {
        await api.patch(`/inventory/${item.id}`, {
          is_hidden: !item.is_hidden,
          ...(approvalToken ? { approvalToken } : {}),
        });

        toast({
          title: item.is_hidden ? "Item unhidden" : "Item hidden",
          description: item.is_hidden
            ? `${item.item_name} is now visible in inventory`
            : `${item.item_name} has been hidden from inventory`,
        });

        refetch();
        onRefresh?.();
      } catch (error) {
        if (error instanceof ApiClientError) {
          if (
            handlePermissionError(error, {
              requestApproval,
              toast,
              retryFn: async (token) => handleToggleHidden(item, token),
              actionDescription: `${item.is_hidden ? "Unhide" : "Hide"} inventory item ${item.item_name}`,
            })
          )
            return;
        }
        toast({
          title: "Error",
          description:
            error instanceof Error
              ? error.message
              : "Failed to update item visibility",
          variant: "destructive",
        });
      }
    },
    [toast, refetch, onRefresh, requestApproval]
  );

  const handleDelete = React.useCallback((item: Product) => {
    setDeleteItem({
      id: item.id || "",
      ndc: item.ndc,
      item_name: item.item_name,
    });
    setDeleteModalOpen(true);
  }, []);

  // Open dispense form panel with prefilled data
  const handleOpenDispense = React.useCallback(
    (ndc: string, itemName: string) => {
      setDispensePrefill({ ndc, itemName, date: new Date() });
      setDispenseFormOpen(true);
    },
    []
  );

  // Open receipt form panel with prefilled data
  const handleOpenReceive = React.useCallback(
    (ndc: string, itemName: string) => {
      setReceiptPrefill({ ndc, itemName, dateReceived: new Date() });
      setReceiptFormOpen(true);
    },
    []
  );

  // Memoize the inventoryItem object for DrugDetailsDrawer to avoid reference
  // churn that causes the drawer's useEffect to re-fire on every render.
  const drawerInventoryItem = React.useMemo(() => {
    const source = editingItem ?? selectedProduct;
    if (!source) return undefined;
    return {
      id: source.id || "",
      item_name: source.item_name,
      drug_type: source.drug_type,
    };
    // Only recompute when the actual item id changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem?.id, selectedProduct?.id]);

  // Check duplicate RX handler
  const handleCheckDuplicateRx = React.useCallback(
    async (rxNumber: string): Promise<boolean> => {
      try {
        const response = await fetch(`${API_URL}/dispenses/check-duplicate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ rx_number: rxNumber }),
        });
        const result = await response.json();
        return result.isDuplicate || false;
      } catch (error) {
        logger.error("Check duplicate RX failed", error, {
          operation: "check_duplicate_rx",
        });
        return false;
      }
    },
    []
  );

  // Check stock handler
  const handleCheckStock = React.useCallback(
    async (ndc: string, quantity: number) => {
      try {
        const response = await fetch(`${API_URL}/dispenses/check-stock`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ ndc, quantity: Number(quantity) }),
        });
        const result = await response.json();

        if (!response.ok) {
          console.error("Check stock error:", result.error);
          return {
            available: 0,
            willTriggerReorder: false,
            expiringItems: [],
          };
        }

        return result;
      } catch (error) {
        logger.error("Check stock failed", error, {
          operation: "check_stock",
          metadata: { ndc },
        });
        return {
          available: 0,
          willTriggerReorder: false,
          expiringItems: [],
        };
      }
    },
    []
  );

  const handleCreateDispense = React.useCallback(
    async (
      data: DispenseFormData,
      createContext?: DispenseCreateContext,
      approvalToken?: string
    ) => {
      trackAction("create_dispense_started", "ui", { ndc: data.ndc });

      try {
        const result = await createDispense({
          ...(data as Record<string, unknown>),
          ...(approvalToken ? { approvalToken } : {}),
        });

        // Create note for this dispense if provided
        const noteContent = createContext?.note?.trim();
        if (noteContent && result.dispense?.id) {
          try {
            await createNote({
              entityType: "dispense",
              entityId: result.dispense.id,
              content: noteContent,
            });
          } catch (noteError) {
            console.error("Create note failed:", noteError);
            toast({
              title: "Dispense created, note failed",
              description:
                noteError instanceof ApiClientError
                  ? noteError.message
                  : "Could not save the note. You can add it later from the dispense.",
              variant: "destructive",
            });
          }
        }

        trackAction("create_dispense_success", "ui");
        showSuccessToast(
          `Successfully logged dispense for ${data.item_name}`,
          "Dispense Created"
        );

        if (result.isDuplicate) {
          toast({
            title: "Duplicate RX Number",
            description: result.warning,
          });
        }

        refetch();
        onRefresh?.();
      } catch (error) {
        if (error instanceof ApiClientError) {
          if (
            handlePermissionError(error, {
              requestApproval,
              toast,
              retryFn: async (token) =>
                handleCreateDispense(data, createContext, token),
              actionDescription: `Create dispense for ${data.item_name}`,
            })
          )
            throw error;
        }
        logger.error("Create dispense failed", error, {
          operation: "create_dispense",
          metadata: { ndc: data.ndc },
        });
        throw error;
      }
    },
    [toast, refetch, onRefresh, requestApproval]
  );

  // Lean receipt handler for the drawer — no per-row toast or refresh.
  // The drawer shows a summary toast and owns the approval-modal retry
  // flow; we just forward the optional token and let errors bubble up.
  const handleCreateReceiptRow = React.useCallback(
    async (data: ReceiptFormData, approvalToken?: string) => {
      const payload = {
        ...(data as Record<string, unknown>),
        ...(approvalToken ? { approvalToken } : {}),
      };
      await createReceipt(payload);
    },
    []
  );

  // Collapse expanded row on page/sort change
  React.useEffect(() => {
    setExpandedRowId(null);
  }, [pageIndex, sorting]);

  // Handle pagination changes
  React.useEffect(() => {
    setPage(pageIndex + 1);
  }, [pageIndex, setPage]);

  React.useEffect(() => {
    setPageSize(pageSize);
  }, [pageSize, setPageSize]);

  // Handle sorting changes
  React.useEffect(() => {
    if (sorting.length > 0) {
      const sort = sorting[0];
      setServerSorting(sort.id, sort.desc ? "desc" : "asc");
    }
  }, [sorting, setServerSorting]);

  const isReadOnly = userRole === "auditor";

  // editingData is intentionally excluded from deps — inputs use defaultValue
  // so columns don't need to recreate on every keystroke (prevents focus loss).
  // editingData is still passed to createColumns for the initial render values.
  const columns = React.useMemo(
    () =>
      createColumns(
        handleNDCClick,
        handleViewDetails,
        isReadOnly ? undefined : handleEdit,
        editingRowId,
        editingData,
        handleFieldChange,
        handleSaveEdit,
        handleCancelEdit,
        isSaving,
        isReadOnly ? undefined : handleAuditStock,
        undefined, // handleViewActivity removed - now redirects to activity tab
        isReadOnly ? undefined : handleDelete,
        handleEditNotes,
        isReadOnly ? undefined : handleToggleHidden,
        isReadOnly
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      handleEdit,
      editingRowId,
      handleFieldChange,
      handleSaveEdit,
      handleCancelEdit,
      isSaving,
      handleAuditStock,
      handleDelete,
      handleEditNotes,
      handleToggleHidden,
      isReadOnly,
    ]
  );

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const table = useReactTable({
    data,
    columns,
    pageCount: paginationInfo.totalPages,
    state: {
      sorting,
      columnVisibility,
      pagination,
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  // Set column filter for toolbar input display when initialSearch is provided
  React.useEffect(() => {
    if (initialSearch) {
      table.getColumn("item_name")?.setFilterValue(initialSearch);
    }
  }, [initialSearch, table]);

  return (
    <div className="space-y-4">
      <InventoryToolbar
        table={table}
        onRefresh={() => {
          refetch();
          onRefresh?.();
        }}
        onStartBulkAdd={onStartBulkAdd}
        onSearch={setSearch}
        onFilterChange={setFilters}
        userRole={userRole}
        isLoading={isLoading}
      />

      {isLoading && <TableSkeleton columns={7} rows={10} />}

      {!isLoading && data.length === 0 && (
        <div className="rounded-xl border border-dashed border-border/60 bg-muted/20 p-16 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-muted/60">
            <Package className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-base font-semibold text-foreground">
            No drugs found
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Click &quot;Add Drug&quot; to get started
          </p>
        </div>
      )}

      {!isLoading && data.length > 0 && (
        <>
          <div
            className="rounded-xl border bg-surface overflow-hidden"
            data-testid="inventory-table"
          >
            <div
              className="overflow-auto"
              style={{ maxHeight: "calc(100vh - 320px)" }}
            >
              <Table style={{ minWidth: "1200px" }}>
                <TableHeader className="sticky top-0 z-10 [&_tr]:border-b">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const sticky =
                          (
                            header.column.columnDef.meta as
                              | { sticky?: string }
                              | undefined
                          )?.sticky === "right";
                        return (
                          <TableHead
                            key={header.id}
                            className={cn(
                              sticky &&
                                "sticky right-0 z-20 bg-table-header border-l border-border/50"
                            )}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => {
                    const rowId = row.original.id ?? "";
                    const isExpanded = expandedRowId === rowId;
                    const isEditing = editingRowId === rowId;
                    return (
                      <React.Fragment key={row.id}>
                        <TableRow
                          role={!isEditing ? "button" : undefined}
                          tabIndex={!isEditing ? 0 : undefined}
                          aria-expanded={!isEditing ? isExpanded : undefined}
                          aria-controls={
                            !isEditing ? `inventory-panel-${rowId}` : undefined
                          }
                          data-testid={`inventory-row-${row.original.ndc}`}
                          className={cn(
                            !isEditing &&
                              "cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                            !isEditing &&
                              !isExpanded &&
                              "hover:bg-muted/40 transition-colors duration-150",
                            isExpanded && "bg-muted/30 border-b-0"
                          )}
                          onClick={
                            !isEditing ? () => handleRowClick(rowId) : undefined
                          }
                          onKeyDown={
                            !isEditing
                              ? (e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleRowClick(rowId);
                                  }
                                }
                              : undefined
                          }
                        >
                          {row.getVisibleCells().map((cell) => {
                            const sticky =
                              (
                                cell.column.columnDef.meta as
                                  | { sticky?: string }
                                  | undefined
                              )?.sticky === "right";
                            return (
                              <TableCell
                                key={cell.id}
                                className={cn(
                                  sticky &&
                                    "sticky right-0 bg-background border-l border-border/50"
                                )}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>

                        {/* Expanded action panel — always in DOM for smooth animation */}
                        <tr
                          className={cn(
                            isExpanded
                              ? "border-b border-border/30 bg-muted/20"
                              : "border-0"
                          )}
                        >
                          <td
                            colSpan={table.getVisibleLeafColumns().length}
                            className="p-0"
                          >
                            <div
                              id={`inventory-panel-${rowId}`}
                              aria-hidden={!isExpanded}
                              className="grid transition-[grid-template-rows] duration-200 ease-out"
                              style={{
                                gridTemplateRows: isExpanded ? "1fr" : "0fr",
                              }}
                            >
                              <div
                                className="overflow-hidden"
                                {...(!isExpanded ? { inert: true } : {})}
                              >
                                <InventoryRowExpandedPanel
                                  product={row.original}
                                  onReceive={handleOpenReceive}
                                  onDispense={handleOpenDispense}
                                  onViewDetails={handleViewDetails}
                                  onEdit={handleEdit}
                                  onEditNotes={handleEditNotes}
                                  onAudit={handleAuditStock}
                                  onClose={() => setExpandedRowId(null)}
                                  readOnly={isReadOnly}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
          <DataTablePagination table={table} />
        </>
      )}

      {selectedNDC && (
        <DrugDetailsDrawer
          open={drugDetailsOpen}
          onOpenChange={(open) => {
            setDrugDetailsOpen(open);
            if (!open) {
              setSelectedNDC(null);
              setSelectedProduct(null);
            }
          }}
          ndc={selectedNDC}
          mode={drawerMode}
          inventoryItem={drawerInventoryItem}
          stockInfo={
            selectedProduct
              ? {
                  stock_calculated: selectedProduct.stock_calculated,
                  status: selectedProduct.status,
                }
              : undefined
          }
          onEditSuccess={handleEditSuccess}
          onReceive={handleOpenReceive}
          onDispense={handleOpenDispense}
        />
      )}

      <InventoryAuditModal
        open={auditModalOpen}
        onClose={() => {
          setAuditModalOpen(false);
          setPreselectedItemId(null);
        }}
        onSuccess={handleAuditSuccess}
        inventoryItems={data.map((item) => ({
          id: item.id || "",
          ndc: item.ndc,
          item_name: item.item_name,
          stock_calculated: item.stock_calculated,
        }))}
        preselectedItemId={preselectedItemId}
      />

      {/* REMOVED: Activity drawer - now redirects to dedicated activity tab */}
      {/* {activityItem && (
        <ActivityDrawer
          open={activityDrawerOpen}
          onClose={() => {
            setActivityDrawerOpen(false);
            setActivityItem(null);
          }}
          itemId={activityItem.id}
          itemName={activityItem.name}
          ndc={activityItem.ndc}
        />
      )} */}

      {notesItem && (
        <NotesThread
          open={notesModalOpen}
          onClose={() => {
            setNotesModalOpen(false);
            setNotesItem(null);
          }}
          entityType="inventory"
          entityId={notesItem.id || ""}
          ndc={notesItem.ndc}
          itemName={notesItem.item_name}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteInventoryModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteItem(null);
        }}
        inventoryItem={deleteItem}
        onSuccess={() => {
          refetch();
          onRefresh?.();
        }}
      />

      {/* Dispense Form Panel */}
      <DispenseFormPanel
        open={dispenseFormOpen}
        onOpenChange={(open) => {
          setDispenseFormOpen(open);
          if (!open) {
            setDispensePrefill(undefined);
          }
        }}
        onSubmit={handleCreateDispense}
        onCheckDuplicateRx={handleCheckDuplicateRx}
        onCheckStock={handleCheckStock}
        initialData={dispensePrefill}
        mode="create"
        confirmStockEnabled={confirmStockEnabled}
      />

      <ReceiptFormPanel
        open={receiptFormOpen}
        onOpenChange={(open) => {
          setReceiptFormOpen(open);
          if (!open) {
            setReceiptPrefill(undefined);
          }
        }}
        mode="create"
        onSubmitRow={handleCreateReceiptRow}
        onAllSubmitted={() => {
          refetch();
          onRefresh?.();
        }}
        initialData={receiptPrefill}
      />
    </div>
  );
}
